package com.kinoshazam.taste

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
enum class TasteDuration {
    @SerialName("short")
    SHORT,

    @SerialName("standard")
    STANDARD,

    @SerialName("long")
    LONG,
}

@Serializable
enum class TasteCompany {
    @SerialName("solo")
    SOLO,

    @SerialName("couple")
    COUPLE,

    @SerialName("trio")
    TRIO,

    @SerialName("group")
    GROUP,

    @SerialName("hall")
    HALL,

    @SerialName("family")
    FAMILY,
}

fun TasteCompany.defaultPartyCapacity(): Int? = when (this) {
    TasteCompany.HALL -> 0
    TasteCompany.COUPLE -> 2
    TasteCompany.TRIO -> 3
    TasteCompany.GROUP -> 4
    else -> null
}

fun TasteCompany.isPartyCompany(): Boolean = defaultPartyCapacity() != null

@Serializable
enum class TasteMood {
    @SerialName("light")
    LIGHT,

    @SerialName("tense")
    TENSE,

    @SerialName("funny")
    FUNNY,

    @SerialName("surprise")
    SURPRISE,
}

fun TasteMood.wireValue(): String = when (this) {
    TasteMood.LIGHT -> "light"
    TasteMood.TENSE -> "tense"
    TasteMood.FUNNY -> "funny"
    TasteMood.SURPRISE -> "surprise"
}

fun TasteCompany.wireValue(): String = when (this) {
    TasteCompany.SOLO -> "solo"
    TasteCompany.COUPLE -> "couple"
    TasteCompany.TRIO -> "trio"
    TasteCompany.GROUP -> "group"
    TasteCompany.HALL -> "hall"
    TasteCompany.FAMILY -> "family"
}

@Serializable
enum class TasteSignalKind {
    @SerialName("opened")
    OPENED,

    @SerialName("played")
    PLAYED,

    @SerialName("skipped")
    SKIPPED,

    @SerialName("already_watched")
    ALREADY_WATCHED,

    @SerialName("dismissed")
    DISMISSED,

    @SerialName("saved")
    SAVED,

    @SerialName("not_interested")
    NOT_INTERESTED,

    @SerialName("accepted")
    ACCEPTED,
}

object TasteTags {
    const val HORROR = "horror"
    const val VIOLENCE = "violence"
    const val SLOW = "slow"
    const val ROMANCE = "romance"
    const val ANIMATION = "animation"
}

@Serializable
data class TasteTitleRef(
    val videoId: String,
    val isTvShow: Boolean,
    val title: String? = null,
    val imageUrl: String? = null,
)

@Serializable
data class TasteSessionDto(
    val id: String,
    val duration: TasteDuration,
    val company: TasteCompany,
    val mood: TasteMood,
    val excludeTags: List<String> = emptyList(),
    val noSubtitles: Boolean = false,
    val excludeWatched: Boolean = true,
    val highRating: Boolean = false,
    val createdAt: String,
)

@Serializable
data class TasteSessionRequest(
    val duration: TasteDuration,
    val company: TasteCompany,
    val mood: TasteMood,
    val excludeTags: List<String> = emptyList(),
    val noSubtitles: Boolean = false,
    val excludeWatched: Boolean = true,
    val highRating: Boolean = false,
)

@Serializable
data class TasteSeedsRequest(
    val items: List<TasteTitleRef>,
)

@Serializable
data class TasteSignalRequest(
    val videoId: String,
    val isTvShow: Boolean,
    val kind: TasteSignalKind,
    val sessionId: String? = null,
)

@Serializable
data class TasteSignalDto(
    val videoId: String,
    val isTvShow: Boolean,
    val kind: TasteSignalKind,
    val sessionId: String? = null,
    val createdAt: String,
)

@Serializable
data class TasteCatalogItem(
    val videoId: String,
    val isTvShow: Boolean,
    val title: String,
    val imageUrl: String? = null,
    val year: Int? = null,
)

@Serializable
data class TasteCompleteness(
    val hasSession: Boolean = false,
    val seedCount: Int = 0,
    val implicitCount: Int = 0,
    val favoriteCount: Int = 0,
    val readyForPick: Boolean = false,
)

@Serializable
data class TasteProfileDto(
    val latestSession: TasteSessionDto? = null,
    val seeds: List<TasteTitleRef> = emptyList(),
    val recentSignals: List<TasteSignalDto> = emptyList(),
    val signalCounts: Map<String, Int> = emptyMap(),
    val favoriteIds: List<TasteTitleRef> = emptyList(),
    val completeness: TasteCompleteness = TasteCompleteness(),
)

@Serializable
data class TasteMergeResult(
    val merged: Boolean = false,
    val profile: TasteProfileDto,
)

object TasteReasonKeys {
    const val SIMILAR_TO_FAVORITE = "similar_to_favorite"
    const val MATCHES_MOOD = "matches_mood"
    const val FAMILY_NIGHT = "family_night"
    const val FITS_TIME = "fits_time"
    const val SURPRISE = "surprise"
    const val POPULAR_TONIGHT = "popular_tonight"
    const val FROM_YOUR_TASTE = "from_your_taste"
    const val TOGETHER = "together"
    const val HIGH_RATING = "high_rating"
}

@Serializable
data class TastePickRequest(
    val exclude: List<TasteTitleRef> = emptyList(),
)

@Serializable
data class TastePickDto(
    val videoId: String,
    val isTvShow: Boolean,
    val title: String,
    val imageUrl: String? = null,
    val year: Int? = null,
    val overview: String? = null,
    val reasonKey: String,
    val reasonTitle: String? = null,
    val sessionId: String? = null,
    val partyCode: String? = null,
)

@Serializable
enum class TastePartyStatus {
    @SerialName("lobby")
    LOBBY,

    @SerialName("picked")
    PICKED,

    @SerialName("locked")
    LOCKED,

    @SerialName("closed")
    CLOSED,
}

@Serializable
enum class TasteVoteKind {
    @SerialName("accepted")
    ACCEPTED,

    @SerialName("skipped")
    SKIPPED,

    @SerialName("not_interested")
    NOT_INTERESTED,

    @SerialName("already_watched")
    ALREADY_WATCHED,
}

@Serializable
data class TastePartyCreateRequest(
    val duration: TasteDuration,
    val company: TasteCompany,
    val mood: TasteMood,
    val excludeTags: List<String> = emptyList(),
    val noSubtitles: Boolean = false,
    val excludeWatched: Boolean = true,
    val highRating: Boolean = false,
    val displayName: String? = null,
    val capacity: Int? = null,
)

@Serializable
data class TastePartyJoinRequest(
    val displayName: String? = null,
)

@Serializable
data class TastePartyReadyRequest(
    val ready: Boolean = true,
)

@Serializable
data class TastePartySettingsRequest(
    val capacity: Int,
)

@Serializable
data class TastePartyMemberDto(
    val displayName: String = "",
    val ready: Boolean = false,
    val isHost: Boolean = false,
    val isYou: Boolean = false,
    val vote: TasteVoteKind? = null,
)

@Serializable
data class TastePartyDto(
    val id: String,
    val code: String,
    val inviteUrl: String = "",
    val capacity: Int,
    val duration: TasteDuration,
    val company: TasteCompany,
    val mood: TasteMood,
    val excludeTags: List<String> = emptyList(),
    val noSubtitles: Boolean = false,
    val excludeWatched: Boolean = true,
    val highRating: Boolean = false,
    val status: TastePartyStatus = TastePartyStatus.LOBBY,
    val members: List<TastePartyMemberDto> = emptyList(),
    val pick: TastePickDto? = null,
    val youAreHost: Boolean = false,
    val youAreMember: Boolean = false,
    val canPick: Boolean = false,
    val canLock: Boolean = false,
    val canForceNext: Boolean = false,
    val acceptedCount: Int = 0,
    val skippedCount: Int = 0,
    val yourVote: TasteVoteKind? = null,
    val expiresAt: String = "",
)

fun TastePartyDto.isLocked(): Boolean = status == TastePartyStatus.LOCKED
